/**
 * Toast notifications — uses an aria-live region for accessibility.
 */
import { useEffect, useState, useCallback } from 'react';
import { clsx } from 'clsx';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
}

let toastCallback: ((type: ToastType, message: string) => void) | null = null;

export function toast(type: ToastType, message: string) {
  toastCallback?.(type, message);
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const remove = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    toastCallback = (type: ToastType, message: string) => {
      const id = crypto.randomUUID();
      setToasts((prev) => [...prev, { id, type, message }]);
      setTimeout(() => remove(id), 5000);
    };
    return () => { toastCallback = null; };
  }, [remove]);

  const icons = {
    success: <CheckCircle className="h-5 w-5 text-[var(--color-success)]" />,
    error: <AlertCircle className="h-5 w-5 text-[var(--color-danger)]" />,
    info: <Info className="h-5 w-5 text-[var(--color-accent)]" />,
  };

  const borderTone = {
    success: 'border-[var(--color-success)]',
    error: 'border-[var(--color-danger)]',
    info: 'border-[var(--color-accent)]',
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className={clsx(
            'flex items-center gap-3 rounded-lg border bg-[var(--color-surface)] px-4 py-3 shadow-[var(--shadow-brutal)]',
            borderTone[t.type],
          )}
          role="status"
        >
          {icons[t.type]}
          <span className="text-sm font-semibold text-[var(--color-ink)]">{t.message}</span>
          <button
            onClick={() => remove(t.id)}
            className="ml-auto rounded-md border border-transparent p-0.5 text-[var(--color-muted)] hover:border-[var(--color-rule)] hover:text-[var(--color-ink)]"
            aria-label="Dismiss notification"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
